import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, RotateCcw, Copy, FileText, Check, History, ChevronLeft, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/lib/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_wonderchat-mobile/artifacts/9pn3gi8c_ChatGPT%20Image%207%20feb%202026%2C%2019_58_01.png";

const renderMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];
    let tableRows = [];

    // --- Auto-detect exercise routines and build table data ---
    const exerciseBlocks = [];
    let currentExercise = null;

    lines.forEach(line => {
        const trimmed = line.trim();
        // Pattern 1: **1. Name** or 1. **Name** — possibly with series/reps on same line
        const exMatch = trimmed.match(/^\*\*(\d+)\.\s*(.+?)\*\*/) ||
                         trimmed.match(/^(\d+)\.\s*\*\*(.+?)\*\*/) ||
                         trimmed.match(/^(\d+)\.\s+\*\*(.+?)\*\*/);
        if (exMatch) {
            if (currentExercise) exerciseBlocks.push(currentExercise);
            let name = exMatch[2].replace(/\*\*/g, '').trim();
            let series = '', reps = '', rest = '';
            // Check for series/reps on same line after the name: — 4 × 6-8 reps or — 4 x 6-8 reps
            const afterName = trimmed.slice(trimmed.indexOf(exMatch[0]) + exMatch[0].length);
            const srSameLine = afterName.match(/(\d+)\s*[×xX]\s*([\d\-]+)\s*(?:reps?)?/i);
            if (srSameLine) {
                series = srSameLine[1];
                reps = srSameLine[2];
            }
            // Clean name: remove trailing " —" or " -" and anything after
            name = name.replace(/\s*[—–-]\s*$/, '').trim();
            currentExercise = { num: exMatch[1], name, series, reps, rest, details: [] };
            return;
        }
        if (currentExercise && /^[-*•]\s/.test(trimmed)) {
            const bullet = trimmed.replace(/^[-*•]\s/, '');
            // Extract series x reps patterns from bullets
            const srMatch = bullet.match(/(\d+)\s*(?:series?|sets?)\s*[×xX]\s*([\d\-]+)\s*(?:reps?)?/i) ||
                            bullet.match(/(\d+)\s*[×xX]\s*([\d\-]+)\s*(?:reps?)/i);
            if (srMatch && !currentExercise.series) {
                currentExercise.series = srMatch[1];
                currentExercise.reps = srMatch[2];
            }
            // Extract rest/descanso
            const restMatch = bullet.match(/[Dd]escanso:?\s*([\d:]+\s*[-–]?\s*[\d:]*\s*(?:min|s|seg)?)/i);
            if (restMatch) {
                currentExercise.rest = restMatch[1].trim();
            }
            return;
        }
        if (currentExercise && trimmed === '') {
            exerciseBlocks.push(currentExercise);
            currentExercise = null;
        }
    });
    if (currentExercise) exerciseBlocks.push(currentExercise);

    // Only render as table if we found 2+ exercises with series data
    const hasExercises = exerciseBlocks.length >= 2 && exerciseBlocks.some(e => e.series);

    const flushList = () => {
        if (listItems.length > 0) {
            elements.push(<ul key={`ul-${elements.length}`} className="list-disc pl-4 my-1 space-y-0.5">{listItems}</ul>);
            listItems = [];
        }
    };

    const flushTable = () => {
        if (tableRows.length > 0) {
            const header = tableRows[0];
            const body = tableRows.slice(1);
            elements.push(
                <div key={`tbl-${elements.length}`} className="overflow-x-auto my-2">
                    <table className="w-full text-xs border border-border/40 rounded-lg overflow-hidden">
                        <thead>
                            <tr className="bg-primary/15">
                                {header.map((cell, c) => (
                                    <th key={c} className="px-3 py-1.5 text-left font-semibold text-foreground border-b border-border/40">{processInline(cell.trim(), `th-${c}`)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {body.map((row, r) => (
                                <tr key={r} className={r % 2 === 0 ? 'bg-card/50' : 'bg-card/30'}>
                                    {row.map((cell, c) => (
                                        <td key={c} className="px-3 py-1.5 text-foreground/80 border-b border-border/20">{processInline(cell.trim(), `td-${r}-${c}`)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
            tableRows = [];
        }
    };

    const cleanUrlLabel = (url) => {
        try {
            const u = new URL(url);
            const host = u.hostname.replace('www.', '');
            if (host.includes('matterport')) return 'View 3D Tour';
            if (host.includes('youtube') || host.includes('youtu.be')) return 'Watch Video';
            if (host.includes('instagram')) return 'View on Instagram';
            if (host.includes('facebook')) return 'View on Facebook';
            if (host.includes('twitter') || host.includes('x.com')) return 'View Post';
            if (host.includes('drive.google')) return 'Open File';
            if (host.includes('docs.google')) return 'Open Document';
            return `Open ${host}`;
        } catch { return 'Open link'; }
    };

    const isImageUrl = (url) => /\.(png|jpg|jpeg|gif|webp|svg|bmp)(\?.*)?$/i.test(url);

    const processInline = (line, key) => {
        const parts = [];
        const regex = /(\!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*(.+?)\*\*|(https?:\/\/[^\s<>)]+))/g;
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(line)) !== null) {
            if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
            if (match[2] !== undefined && match[3]) {
                parts.push(<img key={`${key}-${match.index}`} src={match[3]} alt={match[2] || 'image'} className="rounded-lg max-w-full my-2" loading="lazy" />);
            } else if (match[4] && match[5]) {
                if (isImageUrl(match[5])) {
                    parts.push(<img key={`${key}-${match.index}`} src={match[5]} alt={match[4]} className="rounded-lg max-w-full my-2" loading="lazy" />);
                } else {
                    parts.push(<a key={`${key}-${match.index}`} href={match[5]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:text-primary/80">{match[4]} &#8599;</a>);
                }
            } else if (match[6]) {
                parts.push(<strong key={`${key}-${match.index}`}>{match[6]}</strong>);
            } else if (match[7]) {
                if (isImageUrl(match[7])) {
                    parts.push(<img key={`${key}-${match.index}`} src={match[7]} alt="image" className="rounded-lg max-w-full my-2" loading="lazy" />);
                } else {
                    parts.push(<a key={`${key}-${match.index}`} href={match[7]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:text-primary/80">{cleanUrlLabel(match[7])} &#8599;</a>);
                }
            }
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < line.length) parts.push(line.slice(lastIndex));
        return parts.length > 0 ? parts : [line];
    };

    // Track which exercise block we're inside to skip those lines
    let insideExerciseBlock = false;
    let exerciseTableRendered = false;
    let exerciseNumsSeen = new Set();

    lines.forEach((line, i) => {
        const trimmed = line.trim();

        // If we have auto-detected exercises, render them as a table at the right point
        if (hasExercises) {
            const exMatch = trimmed.match(/^\*\*(\d+)\.\s*(.+?)\*\*/) ||
                             trimmed.match(/^(\d+)\.\s*\*\*(.+?)\*\*/) ||
                             trimmed.match(/^(\d+)\.\s+\*\*(.+?)\*\*/);
            if (exMatch) {
                exerciseNumsSeen.add(exMatch[1]);
                if (!exerciseTableRendered) {
                    // Render the exercise table here
                    flushList();
                    const hasRest = exerciseBlocks.some(e => e.rest);
                    elements.push(
                        <div key={`extbl-${i}`} className="overflow-x-auto my-3">
                            <table className="w-full text-xs border border-border/40 rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-primary/15">
                                        <th className="px-2 py-1.5 text-left font-semibold text-foreground border-b border-border/40">#</th>
                                        <th className="px-2 py-1.5 text-left font-semibold text-foreground border-b border-border/40">Ejercicio</th>
                                        <th className="px-2 py-1.5 text-center font-semibold text-foreground border-b border-border/40">Series</th>
                                        <th className="px-2 py-1.5 text-center font-semibold text-foreground border-b border-border/40">Reps</th>
                                        {hasRest && <th className="px-2 py-1.5 text-center font-semibold text-foreground border-b border-border/40">Descanso</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {exerciseBlocks.map((ex, r) => (
                                        <tr key={r} className={r % 2 === 0 ? 'bg-card/50' : 'bg-card/30'}>
                                            <td className="px-2 py-1.5 text-foreground/60 border-b border-border/20">{ex.num}</td>
                                            <td className="px-2 py-1.5 text-foreground/90 border-b border-border/20 font-medium">{ex.name}</td>
                                            <td className="px-2 py-1.5 text-foreground/80 border-b border-border/20 text-center">{ex.series || '-'}</td>
                                            <td className="px-2 py-1.5 text-foreground/80 border-b border-border/20 text-center">{ex.reps || '-'}</td>
                                            {hasRest && <td className="px-2 py-1.5 text-foreground/80 border-b border-border/20 text-center">{ex.rest || '-'}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                    exerciseTableRendered = true;
                }
                insideExerciseBlock = true;
                return;
            }
            // Skip bullet lines that are part of exercise blocks
            if (insideExerciseBlock && /^[-*•]\s/.test(trimmed)) return;
            if (insideExerciseBlock && trimmed === '') {
                insideExerciseBlock = false;
                return;
            }
            insideExerciseBlock = false;
        }

        // Markdown table row (pipes)
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            flushList();
            const stripped = trimmed.replace(/\s/g, '');
            if (/^\|[-:]+(\|[-:]+)+\|$/.test(stripped)) return;
            const cells = trimmed.slice(1, -1).split('|');
            tableRows.push(cells);
            return;
        } else {
            flushTable();
        }

        // Horizontal rule ---
        if (/^-{3,}$/.test(trimmed)) {
            flushList();
            elements.push(<hr key={`hr-${i}`} className="border-border/30 my-2" />);
            return;
        }

        // List items
        if (/^[-*•]\s/.test(trimmed) || /^\d+\.\s/.test(trimmed)) {
            const content = trimmed.replace(/^[-*•]\s|^\d+\.\s/, '');
            listItems.push(<li key={`li-${i}`}>{processInline(content, `li-${i}`)}</li>);
        } else {
            flushList();
            if (trimmed === '') {
                // skip consecutive blank lines
            } else {
                elements.push(<p key={`p-${i}`} className="my-0.5">{processInline(trimmed, `p-${i}`)}</p>);
            }
        }
    });
    flushList();
    flushTable();
    return elements;
};

export const ChatModal = ({ isOpen, onClose, userCode, initialShowHistory }) => {
    const t = useTranslations();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [chatlogId, setChatlogId] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historySearch, setHistorySearch] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const fallbackTips = [t.tip1, t.tip2, t.tip3, t.tip4];

    const fetchHistory = async (search = '') => {
        if (!userCode) return;
        setHistoryLoading(true);
        try {
            const q = search ? `&search=${encodeURIComponent(search)}` : '';
            const resp = await fetch(`${API_URL}/api/chat/history?user_code=${encodeURIComponent(userCode)}&limit=30${q}`);
            const data = await resp.json();
            if (resp.ok) setHistoryLogs(data.logs || []);
        } catch { /* silent */ }
        finally { setHistoryLoading(false); }
    };

    const deleteHistoryEntry = async (log) => {
        if (!log.id) return;
        try {
            const resp = await fetch(`${API_URL}/api/chat/history/${log.id}?user_code=${encodeURIComponent(userCode)}`, { method: 'DELETE' });
            if (resp.ok) setHistoryLogs(prev => prev.filter(l => l.id !== log.id));
        } catch { /* silent */ }
    };

    const openHistoryConversation = (log) => {
        const cid = log.chatlog_id;
        const related = (cid ? historyLogs.filter(l => l.chatlog_id === cid) : [log])
            .slice()
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        const msgs = [];
        related.forEach(l => {
            if (l.question) msgs.push({ role: 'user', content: l.question });
            if (l.response) msgs.push({ role: 'assistant', content: l.response });
        });
        setMessages(msgs);
        setChatlogId(cid || null);
        setShowHistory(false);
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setTimeout(() => inputRef.current?.focus(), 300);
            if (initialShowHistory) {
                setShowHistory(true);
                fetchHistory();
            }
            // Fetch random suggestions from user history
            fetch(`${API_URL}/api/chat/suggestions?count=4`)
                .then(r => r.json())
                .then(d => {
                    if (d.suggestions && d.suggestions.length >= 2) {
                        setSuggestions(d.suggestions);
                    } else {
                        setSuggestions(fallbackTips);
                    }
                })
                .catch(() => setSuggestions(fallbackTips));
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        const question = input.trim();
        if (!question || loading) return;

        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: question }]);
        setLoading(true);

        try {
            const resp = await fetch(`${API_URL}/api/chat/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question, chatlog_id: chatlogId, user_code: userCode }),
            });
            const data = await resp.json();
            if (resp.ok) {
                setChatlogId(data.chatlog_id);
                setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
            } else {
                setMessages(prev => [...prev, { role: 'assistant', content: 'Error: Could not get a response. Please try again.' }]);
            }
        } catch {
            setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
        } finally {
            setLoading(false);
        }
    };

    const handleRestart = () => {
        setMessages([]);
        setChatlogId(null);
    };

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch { /* fallback */ }
    };

    const handleSinglePDF = async (text) => {
        try {
            const resp = await fetch(`${API_URL}/api/chat/export-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: [{ role: 'assistant', content: text }] }),
            });
            if (resp.ok) {
                const today = new Date().toISOString().slice(0, 10);
                downloadBlob(await resp.blob(), `gymbro-rutina-${today}.pdf`);
            }
        } catch { /* silent */ }
    };

    const MessageActions = ({ content, index }) => {
        const [copied, setCopied] = useState(false);

        const onCopy = async () => {
            await handleCopy(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        };

        return (
            <div className="flex items-center gap-4 mt-2 pt-2 border-t border-border/30" data-testid={`msg-actions-${index}`}>
                <button onClick={onCopy} className="flex items-center gap-1 text-xs text-foreground/40 hover:text-foreground/70 transition-colors" data-testid={`msg-copy-${index}`}>
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                </button>
                <button onClick={() => handleSinglePDF(content)} className="flex items-center gap-1 text-xs text-foreground/40 hover:text-foreground/70 transition-colors" data-testid={`msg-pdf-${index}`}>
                    <FileText className="w-3.5 h-3.5" />
                    PDF
                </button>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="chat-modal">
            {/* Header */}
            <div className="bg-black p-3 flex items-center gap-3 shrink-0">
                <a href="https://mygymbro.es/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 flex-1" data-testid="chat-logo-link">
                    <img src={LOGO_URL} alt="GymBro" className="w-9 h-9 object-contain" />
                    <div>
                        <h3 className="font-semibold text-primary-foreground text-sm hover:text-primary transition-colors">GymBro</h3>
                        <p className="text-xs text-primary-foreground/70">{t.alwaysOnline}</p>
                    </div>
                </a>
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs text-primary-foreground/70">{t.online}</span>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRestart}
                    className="text-primary-foreground hover:bg-primary-foreground/20"
                    title="Clear chat"
                    data-testid="chat-restart-btn"
                >
                    <RotateCcw className="w-5 h-5" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }}
                    className="text-primary-foreground hover:bg-primary-foreground/20"
                    title="Chat history"
                    data-testid="chat-history-btn"
                >
                    <History className="w-5 h-5" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-primary-foreground hover:bg-primary-foreground/20"
                    data-testid="chat-close-btn"
                >
                    <X className="w-5 h-5" />
                </Button>
            </div>

            {/* History panel */}
            {showHistory && (
                <div className="flex-1 overflow-y-auto bg-background" data-testid="chat-history-panel">
                    <div className="p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <Button variant="ghost" size="icon" onClick={() => setShowHistory(false)} className="shrink-0" data-testid="history-back-btn">
                                <ChevronLeft className="w-5 h-5" />
                            </Button>
                            <h3 className="text-foreground font-semibold text-base">Your Chat History</h3>
                        </div>

                        {/* Search bar */}
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40 pointer-events-none" />
                            <input
                                type="text"
                                value={historySearch}
                                onChange={(e) => { setHistorySearch(e.target.value); fetchHistory(e.target.value); }}
                                placeholder="Buscar en tu historial..."
                                className="w-full bg-card border border-border rounded-xl pl-10 pr-9 py-2 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:border-primary/60 transition-colors"
                                data-testid="history-search-input"
                            />
                            {historySearch && (
                                <button
                                    onClick={() => { setHistorySearch(''); fetchHistory(''); }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground/70"
                                    data-testid="history-search-clear"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {historyLoading ? (
                            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-foreground/40" /></div>
                        ) : historyLogs.length === 0 ? (
                            <p className="text-center text-foreground/50 py-12 text-sm">{historySearch ? 'Sin resultados' : 'No conversations yet'}</p>
                        ) : (
                            <div className="space-y-3">
                                {historyLogs.map((log, i) => (
                                    <div
                                        key={i}
                                        onClick={() => openHistoryConversation(log)}
                                        className="bg-card border border-border rounded-xl p-3 cursor-pointer hover:border-primary/60 hover:bg-card/80 transition-colors"
                                        data-testid={`history-item-${i}`}
                                    >
                                        <div className="flex items-start gap-2 mb-2">
                                            <div className="bg-primary/20 rounded-full px-2 py-0.5 text-primary text-[10px] font-medium shrink-0">You</div>
                                            <p className="text-foreground text-sm flex-1">{log.question}</p>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); deleteHistoryEntry(log); }}
                                                className="shrink-0 text-foreground/30 hover:text-destructive transition-colors p-1 -mt-1 -mr-1"
                                                title="Borrar"
                                                data-testid={`history-delete-${i}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <div className="bg-foreground/10 rounded-full px-2 py-0.5 text-foreground/70 text-[10px] font-medium shrink-0">GYMBRO</div>
                                            <p className="text-foreground/70 text-xs line-clamp-3">{log.response}</p>
                                        </div>
                                        <p className="text-foreground/30 text-[10px] mt-2 text-right">
                                            {new Date(log.timestamp).toLocaleString()}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Messages area */}
            {!showHistory && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages-area">
                {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full gap-6 text-center px-4">
                        <img src={LOGO_URL} alt="GymBro" className="w-20 h-20 object-contain opacity-60" />
                        <div>
                            <p className="text-foreground/80 font-medium mb-1">{t.chatSubtitle}</p>
                            <p className="text-foreground/50 text-sm">{t.tryAsking}</p>
                        </div>
                        <div className="flex flex-wrap justify-center gap-2">
                            {(suggestions.length > 0 ? suggestions : fallbackTips).map((tip, i) => (
                                <button
                                    key={i}
                                    onClick={() => setInput(tip)}
                                    className="text-xs px-3 py-1.5 rounded-full border border-border text-foreground/70 hover:border-primary hover:text-primary transition-colors"
                                    data-testid={`chat-tip-${i}`}
                                >
                                    {tip}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => { setShowHistory(true); fetchHistory(); }}
                            className="flex items-center gap-2 text-sm text-foreground/50 hover:text-primary transition-colors mt-2"
                            data-testid="chat-history-link"
                        >
                            <History className="w-4 h-4" />
                            View your chat history
                        </button>
                    </div>
                )}

                {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                            className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                msg.role === 'user'
                                    ? 'bg-primary text-primary-foreground rounded-br-md'
                                    : 'bg-card border border-border text-foreground rounded-bl-md'
                            }`}
                            data-testid={`chat-message-${i}`}
                        >
                            <div className="whitespace-pre-wrap">
                                {msg.role === 'user' ? msg.content : renderMarkdown(msg.content)}
                            </div>
                            {msg.role === 'assistant' && <MessageActions content={msg.content} index={i} />}
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="flex justify-start">
                        <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3">
                            <div className="flex gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="w-2 h-2 rounded-full bg-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                                <span className="w-2 h-2 rounded-full bg-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            )}

            {/* Input area */}
            <div className="shrink-0 border-t border-border bg-background p-3">
                <form onSubmit={handleSend} className="flex gap-2 max-w-3xl mx-auto">
                    <input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={t.tip1}
                        disabled={loading}
                        className="flex-1 bg-card border border-border rounded-full px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid="chat-input"
                    />
                    <Button
                        type="submit"
                        size="icon"
                        disabled={!input.trim() || loading}
                        className="rounded-full bg-primary hover:bg-primary/90 shrink-0"
                        data-testid="chat-send-btn"
                    >
                        <Send className="w-4 h-4" />
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default ChatModal;
